import { useTheme, Tooltip, Box } from '@mui/material';
import { BaseAssetProps } from '../types';
import { AssetWrapper } from '../assetWrapper/AssetWrapper';
import { ValveScadaIcon } from 'src/assets/icons/SCADA/assetsComponents/ValveScadaIcon';
import { useGetAssetByIdQuery, useGetMultipleLatestReadings } from 'src/hooks/reactQueryApiHooks';
import { useMemo } from 'react';
import { useAuthentication } from 'src/features/authentication/context';
import { AssignedTag } from 'src/features/sites/types/types';
import { STATE } from 'src/features';
import { getStateOrModeData } from 'src/features/widgets/utils';
import { useValveData } from 'src/features/sites/hooks';
import { AssetNameLabel } from '../../assetNameLabel/AssetNameLabel';

type Props = BaseAssetProps & {};

export const Valve = ({ selected, width, height, data, id }: Props) => {
  const {
    customerId: { value: customerId },
  } = useAuthentication();

  const stableParams = useMemo(
    () => ({
      customerId: customerId!,
      siteId: data?.siteId,
      assetId: data?.assetId,
    }),
    [customerId, data?.siteId, data?.assetId]
  );

  const { asset } = useGetAssetByIdQuery(stableParams);

  const { valveLiveData } = useValveData({ asset: asset! });
  const theme = useTheme();

  const liveState = valveLiveData.state;

  const itemsForHook = useMemo(
    () =>
      asset?.assignedTags?.map((tag) => ({
        id: tag.id,
        assignedAlarmGatewayId: tag.assignedGatewayId ?? null,
        assignedAlarmTagId: tag.assignedTagId ?? null,
      })) ?? [],
    [asset?.assignedTags]
  );

  const { readingsMap } = useGetMultipleLatestReadings({ items: itemsForHook });

  const readingsArray = useMemo(() => {
    if (!asset?.assignedTags) return [];
    return Object.entries(readingsMap)
      .map(([id, values]) => {
        const tag = asset?.assignedTags?.find((t) => t.id === id);
        if (!tag) return null;
        return { gatewayId: tag.assignedGatewayId!, values };
      })
      .filter(Boolean) as { gatewayId: string; values: any }[];
  }, [readingsMap, asset?.assignedTags]);

  const filteredStates = useMemo(
    () =>
      asset?.assignedTags
        ?.filter((tag: AssignedTag) => tag.tagName.toLowerCase() === STATE)
        .sort((a, b) => a.tagIndicatorPosition - b.tagIndicatorPosition),
    [asset?.assignedTags]
  );

  const displayState =
    liveState && liveState !== '-'
      ? liveState
      : getStateOrModeData({
          data: filteredStates ?? [],
          latestReadings: readingsArray,
        });

  return (
    <AssetWrapper selected={selected} width={width} height={height} id={id}>
      <Box
        data-sm={displayState || 'No state'}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          py: 0.5,
          boxSizing: 'border-box',
        }}
      >
        <Tooltip title={displayState || 'No state'}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <ValveScadaIcon
              width={width}
              height={height! * 0.7}
              fill={displayState === 'Opened' ? theme.palette.primary.main : '#ECEDEF'}
            />
          </Box>
        </Tooltip>

        {data?.isDisplayName && (
          <Box sx={{ textAlign: 'center' }}>
            <AssetNameLabel assetName={asset?.assetName} />
          </Box>
        )}
      </Box>
    </AssetWrapper>
  );
};
